import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { get, post } from "@/lib/api";
import { approvalPath, artifactPath, runPath } from "@/lib/moduleLinks";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FIELDS, TASK_FIELDS, REQUIRED, prepareTaskParams } from "./agentForms";

const controlClass = "og-focus w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm";

const recordRoutes = {
  lead_id: "/crm/leads",
  opportunity_id: "/crm/opportunities",
  meeting_id: "/meetings",
};

function RecordPicker({ fieldKey, value, onChange, disabled, inputProps }) {
  const { t } = useTranslation();
  const { orgId } = useAuth();
  const path = recordRoutes[fieldKey];
  const records = useQuery({
    queryKey: ["agent-records", orgId, fieldKey],
    queryFn: () => get(path, path === "/meetings" ? undefined : { params: { page_size: 100 } }),
    enabled: !!orgId,
  });
  const items = path === "/meetings" ? records.data : records.data?.items;
  const options = Array.isArray(items) ? items : [];
  return <div className="space-y-2">
    {records.isPending ? <p className="text-xs text-muted-foreground">{t("agentsUi.loadingRecords")}</p> :
      records.isError || !Array.isArray(items) ? <p role="alert" className="text-xs text-destructive">{t("agentsUi.recordsError")}</p> :
        <select aria-label={t("agentsUi.chooseRecord")} className={controlClass} disabled={disabled}
          value={options.some((item) => item.id === value) ? value : ""}
          onChange={(event) => onChange(event.target.value)} data-testid={`agent-record-${fieldKey}`}>
          <option value="">{t("agentsUi.chooseRecord")}</option>
          {options.map((item) => <option key={item.id} value={item.id}>{item.title || item.name || item.id}</option>)}
        </select>}
    <Input {...inputProps} value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}
      placeholder={t("agentsUi.recordIdHint")} />
  </div>;
}

export default function AgentRunForm({ agent, allowed, reason, prefill }) {
  const { t } = useTranslation();
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const tasks = agent.tasks.filter((task) => TASK_FIELDS[task.task_type] && agent.contract?.tasks?.find((entry) => entry.task_type === task.task_type && entry.has_input_schema));
  const [taskType, setTaskType] = useState(prefill?.taskType && tasks.some((task) => task.task_type === prefill.taskType) ? prefill.taskType : tasks[0]?.task_type || "");
  const [values, setValues] = useState(prefill?.values || {});
  const [invalid, setInvalid] = useState("");
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: (params) => post(`/agents/${encodeURIComponent(agent.id)}/run`, { task_type: taskType, params }),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ["agent-runs", orgId, agent.id] });
      queryClient.invalidateQueries({ queryKey: ["agents", orgId] });
    },
  });

  const submit = (event) => {
    event.preventDefault();
    if (!allowed || !taskType || run.isPending) return;
    const prepared = prepareTaskParams(taskType, values);
    if (prepared.invalid) {
      setInvalid(prepared.invalid);
      return;
    }
    setInvalid("");
    setResult(null);
    run.mutate(prepared.params);
  };

  return (
    <section className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-6" data-testid="agent-manual-run">
      <h2 className="text-lg font-semibold">{t("agentsUi.runTitle")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("agentsUi.runHint")}</p>
      {prefill && <p className="mt-2 text-xs text-muted-foreground" data-testid="agent-prefill-hint">{t("agentsUi.prefilledHint")}</p>}
      {!allowed && <p className="mt-3 rounded-md bg-muted p-3 text-sm" role="status" data-testid="agent-run-unavailable">{reason || t("agentsUi.runUnavailable")}</p>}
      {tasks.length === 0 && <p className="mt-3 text-sm" role="status">{t("agentsUi.runUnavailable")}</p>}
      {allowed && tasks.length > 0 && (
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="agent-run-form">
          <div>
            <label className="mb-1.5 block text-sm font-medium" htmlFor="agent-task">{t("agentsUi.task")}</label>
            <select id="agent-task" className={controlClass} value={taskType} disabled={run.isPending}
              onChange={(event) => { setTaskType(event.target.value); setValues({}); setInvalid(""); setResult(null); run.reset(); }}>
              {tasks.map((task) => <option key={task.task_type} value={task.task_type}>{t(`agentsUi.taskNames.${task.task_type}`, { defaultValue: task.description })}</option>)}
            </select>
            {taskType === "analyze_meeting" && <p className="mt-2 text-xs text-muted-foreground">{t("agentsUi.meetingRequirement")}</p>}
            {taskType === "draft_proposal" && <p className="mt-2 text-xs text-muted-foreground">{t("agentsUi.proposalRequirement")}</p>}
          </div>
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            {TASK_FIELDS[taskType]?.map((key) => {
              const spec = FIELDS[key];
              const label = t(`agentsUi.fields.${key}`);
              const required = REQUIRED[taskType]?.includes(key);
              const wide = ["textarea", "list"].includes(spec.type);
              const props = { id: `agent-field-${key}`, value: values[key] || "", disabled: run.isPending,
                onChange: (event) => { setValues((prev) => ({ ...prev, [key]: event.target.value })); if (invalid === key) setInvalid(""); } };
              return (
                <div key={key} className={`min-w-0 ${wide ? "sm:col-span-2" : ""}`}>
                  <label className="mb-1.5 block text-sm font-medium" htmlFor={props.id}>{label}{required ? " *" : ""}</label>
                  {spec.type === "uuid" ? (
                    <RecordPicker fieldKey={key} value={values[key] || ""} disabled={run.isPending}
                      onChange={(value) => { setValues((prev) => ({ ...prev, [key]: value })); if (invalid === key) setInvalid(""); }}
                      inputProps={{ id: props.id, required }} />
                  ) : spec.type === "select" ? (
                    <select {...props} className={controlClass}>
                      <option value="">{t("agentsUi.defaultValue")}</option>
                      {(key === "objective" && ["draft_outreach"].includes(taskType)
                        ? ["meeting", "discovery", "follow_up", "reactivation", "proposal_follow_up"]
                        : key === "objective" ? ["awareness", "leads", "sales", "engagement"] : spec.options).map((value) =>
                        <option key={value} value={value}>{t(`agentsUi.options.${value}`, { defaultValue: value.replaceAll("_", " ") })}</option>)}
                    </select>
                  ) : wide ? (
                    <Textarea {...props} rows={spec.type === "list" ? 3 : 4} required={required}
                      placeholder={spec.type === "list" ? t("agentsUi.listHint") : undefined} />
                  ) : (
                    <Input {...props} required={required} type={["integer", "number"].includes(spec.type) ? "number" : "text"}
                      min={spec.min} max={taskType === "create_content" && key === "count" ? 5 : spec.max}
                      step={spec.type === "number" ? "any" : spec.type === "integer" ? 1 : undefined} />
                  )}
                  {invalid === key && <p role="alert" className="mt-1 text-xs text-destructive">{t("agentsUi.invalidField", { field: label })}</p>}
                </div>
              );
            })}
          </div>
          <Button type="submit" disabled={run.isPending} data-testid="agent-run-submit" className="w-full sm:w-auto">
            {run.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {run.isPending ? t("agentsUi.running") : t("agentsUi.run")}
          </Button>
          {run.isPending && <p role="status" className="text-sm text-muted-foreground">{t("agentsUi.runningHint")}</p>}
        </form>
      )}
      {run.isError && <p className="mt-4 rounded-md border border-destructive/30 p-3 text-sm text-destructive" role="alert" data-testid="agent-run-error">
        {t("agentsUi.runError")}: {run.error?.message}
      </p>}
      {result && <div className="mt-5 min-w-0 space-y-3 rounded-lg border border-border p-4" data-testid="agent-run-result" role="status">
        <div className="flex flex-wrap items-center gap-2">
          <strong>{result.status === "failed" ? t("agentsUi.runError") : t("agentsUi.result")}</strong>
          <span className="rounded bg-muted px-2 py-0.5 text-xs">{result.status}</span>
        </div>
        {result.run_id && <p className="break-all text-xs text-muted-foreground">{t("agentsUi.runId")}: {result.run_id}</p>}
        {result.summary && <p className="whitespace-pre-wrap text-sm">{result.summary}</p>}
        {result.error && <p className="break-words text-sm text-destructive">{result.error}</p>}
        {result.output && Object.keys(result.output).length > 0 && <>
          <h3 className="text-sm font-medium">{t("agentsUi.output")}</h3>
          <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-muted p-3 text-xs">{JSON.stringify(result.output, null, 2)}</pre>
        </>}
        {result.artifacts?.length > 0 && <>
          <h3 className="text-sm font-medium">{t("agentsUi.artifacts")}</h3>
          <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-muted p-3 text-xs">{JSON.stringify(result.artifacts, null, 2)}</pre>
          <div className="flex flex-wrap gap-3 text-sm" data-testid="agent-artifact-links">
            {result.artifacts.map((artifact, index) => {
              const path = artifactPath(artifact);
              return <React.Fragment key={`${artifact.type}-${artifact.id || index}`}>
                {path && <Link to={path} className="break-all text-primary underline">{artifact.label || t("common.open")} ↗</Link>}
                {artifact.type === "content_item" && artifact.approval_id && <Link to={approvalPath(artifact.approval_id)} className="text-primary underline">{t("agentsUi.approvals")} ↗</Link>}
              </React.Fragment>;
            })}
          </div>
        </>}
        <div className="flex flex-wrap gap-4 text-sm">
          {result.run_id && <Link className="text-primary underline" to={runPath(result.run_id)}>{t("agentsUi.monitoring")}</Link>}
        </div>
      </div>}
    </section>
  );
}